'use client'

import { memo } from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@segway/core'
import { CartAddressInput } from '@segway/core/src/graphql/generated/graphql'

import { UserAddressForm } from '@/components'
import { ItemAddIcon, ItemEditIcon, ItemViewMoreIcon } from '@/components/icons'
import { Button } from '@/ui'

import useCheckoutShipping from '../../../../../../hooks/checkout/useCheckoutShipping'

/**
 * 结算Shipping地址 组件
 */
const CheckoutShipping = () => {
  const {
    shippingAddress,
    addressTitle,
    addressDescription,
    formVisible,
    handleNewAddress,
    cancelNewAddress,
    handleEditAddress,
    formValues,
    saveFormAddress,
    addressListVisible,
    viewMoreBtnVisible,
    shippingId,
    handleEditCustomerAddress,
    customerAddressEditor,
    handleSelectAddressItem,
    closeCustomerAddressEditor,
    closeForm,
    formCancelBtnVisible,
    customerAddressList,
    isLoggedIn,
    isUseAddressListCollapseed,
    triggerViewMore,
    isMx,
  } = useCheckoutShipping()

  const t = useTranslations('Cart')

  return (
    <div className="space-y-2 rounded-[12px] bg-white p-3 xl:space-y-4 xl:p-0">
      <p className="mb-3 text-[16px] xl:text-[20px]">{t('shipping_address')}</p>
      {/* 登录用户 */}
      {/* 用户地址列表 */}
      {addressListVisible &&
        customerAddressList.map((item) => {
          if (item && item.address) {
            const { address, title, desc } = item
            const { id, default_shipping } = address
            const isActive = shippingId === id
            const isEditing = customerAddressEditor.visible && customerAddressEditor.id === id
            return isEditing ? (
              <div
                key={id}
                className={`rounded-[8px] border border-[${isActive ? '#DA291C' : '#E1E1E4'}] px-4 py-3.5 text-[14px] hover:cursor-pointer xl:p-5`}>
                {/* 表单：编辑登录用户的地址 */}
                {address && (
                  <UserAddressForm
                    onSubmit={(values) => {
                      saveFormAddress({ values, isCustomerEditing: true })
                    }}
                    defaultValues={address}
                    saveAsDefaultBtnVisible
                  />
                )}
                <Button
                  onClick={closeCustomerAddressEditor}
                  className="mt-[12px] h-[44px] w-full text-[14px] xl:w-[215px]">
                  Cancel
                </Button>
              </div>
            ) : (
              <div
                onClick={() => {
                  handleSelectAddressItem(id)
                }}
                key={id}
                className={`relative flex items-center justify-between rounded-[8px] border border-[${isActive ? '#DA291C' : '#E1E1E4'}] px-4 py-3.5 text-[14px] hover:cursor-pointer xl:p-5`}>
                <div className="mr-2 flex-1">
                  <p className="text-[16px]">{title}</p>
                  <p className="mt-2 text-[12px] text-[#6E6E73] xl:mt-3 xl:text-[14px]">{desc}</p>
                </div>
                <div
                  onClick={(e) => {
                    handleEditCustomerAddress(id)
                    e.stopPropagation()
                  }}
                  className="flex h-[50px] basis-[50px] cursor-pointer items-center justify-center">
                  <ItemEditIcon />
                </div>
                {default_shipping && (
                  <div
                    className={cn(
                      'absolute right-0 top-0 flex h-[20px] justify-center rounded-bl-[8px] rounded-tr-[8px] bg-[#DA291C] pt-[2px] text-[12px] text-white',
                      isMx ? 'w-[100px]' : 'w-[53px]',
                    )}>
                    {t('default')}
                  </div>
                )}
              </div>
            )
          }
          return null
        })}

      {/* View more button */}
      {viewMoreBtnVisible && (
        <div className="flex cursor-pointer items-center justify-center" onClick={triggerViewMore}>
          <span className="mr-1 text-[12px] text-[#0F0F0F] xl:text-[14px]">
            {isUseAddressListCollapseed ? 'View more' : 'View less'}
          </span>
          <span className={`mb-[-3px] ${isUseAddressListCollapseed ? '' : 'rotate-180'}`}>
            <ItemViewMoreIcon />
          </span>
        </div>
      )}

      {/* TODO: 判断条件优化 */}
      {isLoggedIn && !formVisible && customerAddressEditor.id !== 'new' && (
        <div
          onClick={handleNewAddress}
          className="flex h-[62px] w-full cursor-pointer items-center justify-center rounded-[8px] border border-[#E1E1E4]">
          <ItemAddIcon />
          <span className="mb-[-2px] ml-2 text-[16px]">{t('use_new_address')}</span>
        </div>
      )}
      {/* 表单：登录用户（新增地址） */}
      {customerAddressEditor.visible && customerAddressEditor.id === 'new' && (
        <div className="rounded-[8px] border border-[#E1E1E4] p-3 xl:p-5">
          <div className="mb-3 xl:text-[16px]">{t('use_new_address')}</div>
          <UserAddressForm
            onSubmit={(values) => {
              saveFormAddress({ values })
            }}
            saveAsDefaultBtnVisible
          />
          <Button
            onClick={cancelNewAddress}
            className="mt-[12px] h-[44px] w-full text-[14px] xl:w-[215px]">
            {t('cancel')}
          </Button>
        </div>
      )}

      {/* 游客 */}
      {!isLoggedIn && shippingAddress && !formVisible && (
        <div className="relative flex items-center justify-between rounded-[8px] border border-[#E1E1E4] px-4 py-3.5 text-[14px] hover:cursor-pointer xl:p-5">
          <div className="mr-2">
            <p className="text-[16px]">{addressTitle}</p>
            <p className="mt-2 text-[12px] text-[#6E6E73] xl:mt-3 xl:text-[14px]">
              {addressDescription}
            </p>
          </div>
          <div onClick={handleEditAddress}>
            <ItemEditIcon />
          </div>
        </div>
      )}
      {formVisible && (
        <>
          {/* 表单：游客（新增/编辑 地址) */}
          <UserAddressForm
            onSubmit={(values: CartAddressInput) => {
              saveFormAddress({ values })
            }}
            defaultValues={formValues}
          />
          {formCancelBtnVisible && (
            <Button
              onClick={closeForm}
              className="mt-[12px] h-[44px] w-full text-[14px] xl:w-[215px]">
              {t('cancel')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export default memo(CheckoutShipping)
